/**
 * Calls back at given times on the wall clock, on time in a hidden tab (timer-alerts spec): a hidden page's timers can
 * run up to a minute late (Chrome's intensive throttling), a worker's aren't held back that way. Without workers (tests,
 * old browsers) the page's own timers do it.
 */

export type AlertClock = {
  /** Replaces what was set: each key called back at its time. */
  set: (times: { key: string; at: number }[]) => void;
  stop: () => void;
};

// Each time is kept to the wall clock: a timeout for the moment itself, and a look every 15 seconds, because a sleeping
// laptop stops a timeout's count and it would come as late after waking as the laptop slept.
const WORKER_SOURCE = `
let pending = new Map();
const due = key => {
  if (!pending.has(key)) return;
  clearTimeout(pending.get(key).timer);
  pending.delete(key);
  self.postMessage(key);
};
setInterval(() => pending.forEach((entry, key) => Date.now() >= entry.at && due(key)), 15000);
self.onmessage = event => {
  pending.forEach(entry => clearTimeout(entry.timer));
  pending = new Map();
  (event.data || []).forEach(({ key, at }) => {
    pending.set(key, { at, timer: setTimeout(() => due(key), Math.max(0, at - Date.now())) });
  });
};
`;

const makeWorker = (): Worker | null => {
  try {
    if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || !URL.createObjectURL) return null;
    const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    const worker = new Worker(url);
    URL.revokeObjectURL(url);
    return worker;
  } catch {
    return null;
  }
};

export const makeAlertClock = (onDue: (key: string) => void): AlertClock => {
  const worker = makeWorker();
  if (worker) {
    worker.onmessage = event => onDue(String(event.data));
    return {
      set: times => worker.postMessage(times),
      stop: () => worker.terminate()
    };
  }
  let pending = new Map<string, { at: number; timer: number }>();
  const due = (key: string) => {
    const entry = pending.get(key);
    if (!entry) return;
    window.clearTimeout(entry.timer);
    pending.delete(key);
    onDue(key);
  };
  const look = window.setInterval(() => pending.forEach((entry, key) => Date.now() >= entry.at && due(key)), 15000);
  const clear = () => {
    pending.forEach(entry => window.clearTimeout(entry.timer));
    pending = new Map();
  };
  return {
    set: times => {
      clear();
      times.forEach(({ key, at }) =>
        pending.set(key, { at, timer: window.setTimeout(() => due(key), Math.max(0, at - Date.now())) })
      );
    },
    stop: () => {
      clear();
      window.clearInterval(look);
    }
  };
};
